import { formatTicker, ThemeCompany } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '~/components/theme-controller/utils/theme-utils/theme-utils';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { TruncateTooltip } from '~/components/truncate-tooltip/TruncateTooltip';
import { useEntity } from '~/hooks/use-entities';

import * as S from './CompanyItem.styles';

const MAX_NAME_LENGTH = 30;

type CompanyItemProps = {
  company: ThemeCompany;
  onPillClick: (entityTag: string) => void;
  iconSize?: number;
};

export const CompanyItem = ({
  company,
  onPillClick,
  iconSize = 20,
}: CompanyItemProps) => {
  const { t } = useTranslation(['widget']);
  const themeName = useThemeStore(state => state.themeName);
  const isDark = isDarkTheme(themeName);
  const [imageError, setImageError] = useState(false);
  const { company: tag, relationship } = company;

  const { isFetching, isError, error, data } = useEntity(tag);

  if (isError) {
    throw new Error(error.message);
  }

  if (isFetching || !data?.data) {
    return <S.LoadingSkeleton />;
  }

  const { logo_urls, name } = data.data;
  const logoUrl = isDark ? logo_urls.light_png : logo_urls.dark_png;

  return (
    <TooltipContainer
      label={t('widget:themeOverview.rank', {
        rank: relationship.rank,
      })}
      placement="top-start"
    >
      <S.PillContainer>
        <S.StyledPillDropdown
          label={
            <S.StyledHeader>
              {!!logoUrl && !imageError ? (
                <S.StyledLogoImg
                  src={logoUrl}
                  onError={() => setImageError(true)}
                  $size={iconSize}
                />
              ) : (
                <S.LogoFallback $size={iconSize}>
                  {name.charAt(0)}
                </S.LogoFallback>
              )}
              <S.AssetHeader>
                <TruncateTooltip
                  placement="top"
                  data-testid="company-label"
                  id={`company-label`}
                  text={formatTicker(data.data)}
                  atLength={MAX_NAME_LENGTH}
                />
                <TruncateTooltip
                  placement="bottom"
                  data-testid="company-name"
                  id={`company-name`}
                  text={name}
                  atLength={MAX_NAME_LENGTH}
                />
              </S.AssetHeader>
            </S.StyledHeader>
          }
          value={tag}
          onClickPill={onPillClick}
        />
      </S.PillContainer>
    </TooltipContainer>
  );
};

CompanyItem.Styled = S;
